import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { testIds } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Loader2,
  Check,
  Ban,
  Trash2,
  Mail,
  Phone,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const STATUS_META = {
  pending: {
    label: "In attesa",
    cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    icon: Clock,
  },
  approved: {
    label: "Approvato",
    cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    icon: CheckCircle2,
  },
  rejected: {
    label: "Rifiutato",
    cls: "bg-zinc-800 text-zinc-400 border-zinc-700",
    icon: XCircle,
  },
};

export default function CollaboratorsView() {
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all|pending|approved|rejected

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/collaborators");
    setCollabs(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/collaborators/${id}/status`, { status });
      toast.success(
        status === "approved"
          ? "Collaboratore approvato — email inviata"
          : status === "rejected"
            ? "Collaboratore rifiutato"
            : "Reset a in attesa",
      );
      load();
    } catch {
      toast.error("Errore aggiornamento stato");
    }
  };

  const remove = async (id) => {
    if (
      !window.confirm(
        "Eliminare questo collaboratore? Verrà rimosso anche tutto il suo materiale.",
      )
    )
      return;
    await api.delete(`/collaborators/${id}`);
    toast.success("Collaboratore eliminato");
    load();
  };

  const filtered =
    filter === "all" ? collabs : collabs.filter((c) => c.status === filter);
  const pendingCount = collabs.filter((c) => c.status === "pending").length;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight text-white">
          Collaboratori
        </h2>
        <p className="text-xs text-zinc-500 mt-1">
          Approva o rifiuta i service partner che si iscrivono per caricare il proprio materiale sulla piattaforma.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: "Tutti" },
          { key: "pending", label: `In attesa${pendingCount ? ` (${pendingCount})` : ""}` },
          { key: "approved", label: "Approvati" },
          { key: "rejected", label: "Rifiutati" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`min-h-[36px] px-3 rounded-full text-xs font-semibold uppercase tracking-wider border transition-all ${
              filter === f.key
                ? "bg-rose-600 text-white border-rose-500"
                : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 text-xs font-mono uppercase tracking-widest border border-dashed border-zinc-800 rounded-xl">
          Nessun collaboratore
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div
                key={c.id}
                data-testid={testIds.adminCollabRow(c.id)}
                className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3 sm:p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-zinc-300 shrink-0 font-heading font-bold uppercase">
                    {c.name?.[0] || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-white text-sm break-words">
                        {c.name}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border flex items-center gap-1 ${meta.cls}`}
                      >
                        <Icon size={10} /> {meta.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-zinc-500">
                      <a
                        href={`mailto:${c.email}`}
                        className="flex items-center gap-1 hover:text-rose-400 break-all"
                      >
                        <Mail size={11} /> {c.email}
                      </a>
                      {c.phone && (
                        <a
                          href={`tel:${c.phone}`}
                          className="flex items-center gap-1 hover:text-rose-400"
                        >
                          <Phone size={11} /> {c.phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap gap-2">
                  {c.status !== "approved" && (
                    <button
                      data-testid={testIds.adminCollabApprove(c.id)}
                      onClick={() => setStatus(c.id, "approved")}
                      className="min-h-[36px] px-3 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                    >
                      <Check size={12} /> Approva
                    </button>
                  )}
                  {c.status !== "rejected" && (
                    <button
                      data-testid={testIds.adminCollabReject(c.id)}
                      onClick={() => setStatus(c.id, "rejected")}
                      className="min-h-[36px] px-3 rounded-lg bg-zinc-800 hover:bg-rose-950 border border-zinc-700 text-zinc-400 hover:text-rose-400 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                    >
                      <Ban size={12} /> Rifiuta
                    </button>
                  )}
                  <button
                    data-testid={testIds.adminCollabDelete(c.id)}
                    onClick={() => remove(c.id)}
                    className="min-h-[36px] px-3 rounded-lg bg-zinc-800 hover:bg-rose-950 border border-zinc-700 text-zinc-400 hover:text-rose-400 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5 ml-auto"
                  >
                    <Trash2 size={12} /> Elimina
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
